import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {hasNucleotideInput} from 'search/blast/BLASTUtil';
import {validateBLASTSequence} from 'search/blast/validation';
import {BLASTFilter, BLASTOperator} from 'search/SearchConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Label from 'ui-library/bs/Label';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface BLASTQueryChipsProps {
  filter: BLASTFilter;
  onRemoveSequence: (sequenceToRemove: string, type: 'sequence' | 'entity') => void;
  title?: string;
  canRemoveLastSequence?: boolean;
}

export default function BLASTQueryChips({
  filter,
  onRemoveSequence,
  title = 'Sequences',
  canRemoveLastSequence = true,
}: BLASTQueryChipsProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(true);

  const residueType = hasNucleotideInput(filter.operator as BLASTOperator) ? 'nucleotides' : 'amino acids';

  const sequenceCount = filter.value.textSequences.length + filter.value.apiIdentifiers.length;

  const sequenceChips = filter.value.textSequences.map((textSequence) => {
    const validationState = validateBLASTSequence(textSequence.sequence, filter.operator as BLASTOperator);

    let validationMessage: string = '';
    if (validationState.isNucleotideOnlyAA) {
      validationMessage = 'Sequence only consists of nucleotides.\n';
    } else if (!validationState.isValid) {
      validationMessage = `Invalid ${residueType} or sequence too long.\n`;
    }

    const tooltipSubsequence =
      textSequence.sequence.substr(0, 100) + (textSequence.sequence.length > 100 ? '...' : '');

    return (
      <Chip
        className={validationMessage ? 'chip--warningLighter' : null}
        key={textSequence.name}
        icon="text"
        size={ChipSize.MINI}
        text={textSequence.name}
        onRemove={
          !canRemoveLastSequence && sequenceCount === 1
            ? undefined
            : () => {
                onRemoveSequence(textSequence.name, 'sequence');
              }
        }
        tooltipTitle={`${validationMessage}${tooltipSubsequence}`}
      />
    );
  });

  const entityChips = filter.value.apiIdentifiers.map((apiIdentifier) => (
    <BenchChip
      key={apiIdentifier}
      id={apiIdentifier}
      size={ChipSize.MINI}
      isLink={false}
      onRemove={
        !canRemoveLastSequence && sequenceCount === 1
          ? undefined
          : () => {
              onRemoveSequence(apiIdentifier, 'entity');
            }
      }
    />
  ));

  return (
    <div className={css(styles.basicVerticalMargin)}>
      <div
        className={css(styles.toggle)}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <Label>
          <BenchlingIcon className={css(styles.icon)} icon={isOpen ? 'chevron-down' : 'chevron'} />
          {title} ({sequenceChips.length + entityChips.length})
        </Label>
      </div>
      {isOpen && sequenceChips}
      {isOpen && entityChips}
    </div>
  );
}

const styles = StyleSheet.create({
  toggle: {
    cursor: 'pointer',
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
  icon: {
    marginRight: 8,
  },
  basicVerticalMargin: {
    marginBottom: '15px',
  },
});
